import { useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { AppBar } from "@/components/AppBar";
import { StepIndicator } from "@/components/StepIndicator";
import { ContactIconButton } from "@/components/guide/ContactIconButton";
import { Section1Empathy } from "@/components/guide/Section1Empathy";
import { Section2Regions } from "@/components/guide/Section2Regions";
import { Section3Criterion } from "@/components/guide/Section3Criterion";
import { Section4Communities } from "@/components/guide/Section4Communities";
import { Section5Exit } from "@/components/guide/Section5Exit";
import { useContact } from "@/context/ContactContext";
import { getGuide } from "@/lib/content";
import { createIntakeContext, intakeFromGuide } from "@/lib/intakeContext";
import type { CriterionId } from "@/types/content";
import type { IntakeContactExtra } from "@/types/intake";
import {
  isIdentityId,
  isSchoolId,
  SCHOOL_LABELS,
  type IdentityId,
} from "@/types/navigation";

export function GuidePage() {
  const [params] = useSearchParams();
  const { openIntakeContact } = useContact();
  const [criterion, setCriterion] = useState<CriterionId | null>(null);
  const recRef = useRef<HTMLDivElement>(null);

  const school = params.get("school");
  const identity = params.get("identity");

  if (!isSchoolId(school) || !isIdentityId(identity) || identity === "other") {
    const redirectSchool = isSchoolId(school) ? school : "nus";
    return <Navigate to={`/identity?school=${redirectSchool}`} replace />;
  }

  const resolved = getGuide(school, identity as Exclude<IdentityId, "other">);

  if (!resolved) {
    return <Navigate to={`/identity?school=${school}`} replace />;
  }

  const guide = resolved;

  const handleCriterion = (v: CriterionId) => {
    setCriterion(v);
    window.setTimeout(() => {
      recRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleIntakeContact = (extra?: IntakeContactExtra) => {
    openIntakeContact(
      intakeFromGuide(school, identity as Exclude<IdentityId, "other">, {
        criterion,
        ...extra,
      }),
    );
  };

  const openHeaderIntake = () => {
    openIntakeContact(
      createIntakeContext({
        school,
        identity: identity as Exclude<IdentityId, "other">,
        regions: guide.regions,
        criterion,
        source: "header",
      }),
    );
  };

  return (
    <>
      <AppBar
        title={`居住攻略 · ${SCHOOL_LABELS[school]}`}
        showBack
        right={<ContactIconButton onClick={openHeaderIntake} />}
      />
      <StepIndicator current={3} />

      <Section1Empathy empathyText={guide.empathyText} guide={guide} />
      <Section2Regions schoolId={school} guide={guide} />
      <Section3Criterion value={criterion} onChange={handleCriterion} />
      <div ref={recRef} className="scroll-mt-16" />
      <Section4Communities
        guide={guide}
        criterion={criterion}
        onIntakeContact={handleIntakeContact}
      />
      <Section5Exit onIntakeContact={() => handleIntakeContact({ source: "section5" })} />
    </>
  );
}
